import ApolloErrorBoundary from '@components/ApolloErrorBoundary';
import { FC, Suspense } from 'react';
import { useSuspenseQuery } from '@apollo/client';
import { WriteExecutionLogEntityType, WriteExecutionLogSeverity } from '@/__generated__/graphql';
import GET_MONITORED_TARGET_BAR_CHART from '@features/integrations/graphql/queries/getMonitoredTargetBarChart';
import { Box, Skeleton, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useFormatters } from '@/hooks/useFormatters';
import { Trans } from '@lingui/macro';
import GET_USER_PREFERENCES_REGION from '@features/userPreferences/graphql/queries/getUserPreferencesRegion';
import { getTimeZone } from '@/utils/i18n/timezone';
interface Props {
  integrationId: number;
}

export const COLUMNS_UP_MD = 87;
const CURRENT_DATE = new Date();

const SuspenseComponent: FC<Props> = ({ integrationId }) => {
  const preferenceRegion = getTimeZone(
    useSuspenseQuery(GET_USER_PREFERENCES_REGION).data.user.preferences.region.timezone,
  );
  const { data } = useSuspenseQuery(GET_MONITORED_TARGET_BAR_CHART, {
    variables: {
      integrationId,
      input: {
        severities: [WriteExecutionLogSeverity.Critical],
        range: {
          start: CURRENT_DATE.getDate() - 90,
        },
        entityTypes: [WriteExecutionLogEntityType.Integration],
        groupByDays: true,
        ...(preferenceRegion !== undefined && { preferenceRegion }),
      },
    },
  });
  const theme = useTheme();
  const { formatDate } = useFormatters();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));

  const criticalErrorDays = data.integration.writeExecutionLogGroupedIssues.items.map((item) => ({
    day: item.day,
  }));

  const startDate = new Date(CURRENT_DATE);
  startDate.setUTCHours(0, 0, 0, 0);

  const barChartStates = Array.from({ length: downMd ? 30 : COLUMNS_UP_MD }, (_, i) => {
    const movingDate = new Date(startDate);
    movingDate.setDate(movingDate.getDate() - (downMd ? 30 : COLUMNS_UP_MD) + i + 1);
    const dayTimestamp = movingDate.getTime();
    const hasCriticalError = criticalErrorDays.some((errorDay) => errorDay.day === dayTimestamp);
    return {
      day: dayTimestamp,
      hasCriticalError,
    };
  });

  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: 'repeat(30, 1fr)',
        md: `repeat(${COLUMNS_UP_MD}, 1fr)`,
      }}
      gridTemplateRows="1fr 0 0"
      rowGap={0}
      columnGap="1px"
      borderRadius={1}
      overflow="hidden"
    >
      {barChartStates.map((item) => (
        <Tooltip
          key={item.day}
          title={
            <>
              <Box>{formatDate(item.day, { timeZone: 'UTC' })}</Box>
              <Box>
                {item.hasCriticalError ? <Trans>Incidencias</Trans> : <Trans>Operativo</Trans>}
              </Box>
            </>
          }
        >
          <Box
            height={32}
            bgcolor={({ palette }) =>
              item.hasCriticalError ? palette.error.main : palette.success.main
            }
            borderRadius="2px"
          />
        </Tooltip>
      ))}
    </Box>
  );
};

export const SuspenseFallback: FC = () => {
  return (
    <Box
      display="grid"
      gridTemplateColumns={{
        xs: 'repeat(30, 1fr)',
        md: `repeat(${COLUMNS_UP_MD}, 1fr)`,
      }}
      gridTemplateRows="1fr 0 0"
      gap="1px"
      borderRadius={1}
      overflow="hidden"
    >
      {Array.from({ length: COLUMNS_UP_MD }, (_, index) => (
        <Skeleton
          variant="rounded"
          key={index}
          height={32}
          sx={{
            borderRadius: '2px',
          }}
        />
      ))}
    </Box>
  );
};

export const IntegrationBarChart: FC<Props> = (props) => {
  return (
    <ApolloErrorBoundary variant="section-alert">
      <Suspense fallback={<SuspenseFallback />}>
        <SuspenseComponent {...props} />
      </Suspense>
    </ApolloErrorBoundary>
  );
};
